/**
 * Viem 客户端工具函数
 * 用于创建 L1 和 L2 的 viem 客户端，支持 OP Stack
 */

import { createPublicClient, createWalletClient, http } from 'viem';
import { publicActionsL1, publicActionsL2, walletActionsL1, walletActionsL2 } from 'viem/op-stack';

// 辅助函数：清理环境变量值
const cleanEnvValue = (value) => {
    if (!value) return value;
    return String(value).split('#')[0].trim();
};

/**
 * 创建 L1 公共客户端
 * @returns {import('viem').PublicClient}
 */
export const createL1PublicClient = () => {
    const l1RpcUrl = cleanEnvValue(process.env.REACT_APP_L1_RPC_URL);
    if (!l1RpcUrl) {
        throw new Error('REACT_APP_L1_RPC_URL 未配置');
    }

    return createPublicClient({
        chain: {
            id: Number(cleanEnvValue(process.env.REACT_APP_L1_CHAIN_ID)) || 11155111, // 默认 Sepolia
            name: 'L1',
            network: 'l1',
            nativeCurrency: {
                decimals: 18,
                name: 'Ether',
                symbol: 'ETH',
            },
            rpcUrls: {
                default: {
                    http: [l1RpcUrl],
                },
            },
        },
        transport: http(l1RpcUrl),
    }).extend(publicActionsL1());
};

/**
 * 创建 L2 公共客户端（支持 OP Stack）
 * @returns {import('viem').PublicClient}
 */
export const createL2PublicClient = () => {
    const l2RpcUrl = cleanEnvValue(process.env.REACT_APP_L2_RPC_URL);
    if (!l2RpcUrl) {
        throw new Error('REACT_APP_L2_RPC_URL 未配置');
    }

    const l2ChainId = Number(cleanEnvValue(process.env.REACT_APP_L2_CHAIN_ID));
    if (!l2ChainId) {
        throw new Error('REACT_APP_L2_CHAIN_ID 未配置');
    }

    return createPublicClient({
        chain: {
            id: l2ChainId,
            name: cleanEnvValue(process.env.REACT_APP_L2_NETWORK_NAME) || 'L2',
            network: 'l2',
            nativeCurrency: {
                decimals: 18,
                name: cleanEnvValue(process.env.REACT_APP_L2_NATIVE_CURRENCY_NAME) || 'ETH',
                symbol: cleanEnvValue(process.env.REACT_APP_L2_NATIVE_CURRENCY_SYMBOL) || 'ETH',
            },
            rpcUrls: {
                default: {
                    http: [l2RpcUrl],
                },
            },
            blockExplorers: {
                default: {
                    name: `${cleanEnvValue(process.env.REACT_APP_L2_NETWORK_NAME) || 'L2'} Explorer`,
                    url: cleanEnvValue(process.env.REACT_APP_L2_EXPLORER_URL) || '',
                },
            },
        },
        transport: http(l2RpcUrl),
    }).extend(publicActionsL2());
};

/**
 * 创建 L1 钱包客户端（用于发送交易）
 * @param {import('viem').Account} account - 账户
 * @param {import('viem').Transport} transport - 传输层（通常是 window.ethereum）
 * @returns {import('viem').WalletClient}
 */
export const createL1WalletClient = (transport) => {
    const l1RpcUrl = cleanEnvValue(process.env.REACT_APP_L1_RPC_URL);
    if (!l1RpcUrl) {
        throw new Error('REACT_APP_L1_RPC_URL 未配置');
    }

    return createWalletClient({
        chain: {
            id: Number(cleanEnvValue(process.env.REACT_APP_L1_CHAIN_ID)) || 11155111,
            name: 'L1',
            network: 'l1',
            nativeCurrency: {
                decimals: 18,
                name: 'Ether',
                symbol: 'ETH',
            },
            rpcUrls: {
                default: {
                    http: [l1RpcUrl],
                },
            },
        },
        transport,
    }).extend(walletActionsL1());
};

/**
 * 创建 L2 钱包客户端（支持 OP Stack）
 * @param {import('viem').Account} account - 账户
 * @param {import('viem').Transport} transport - 传输层（通常是 window.ethereum）
 * @returns {import('viem').WalletClient}
 */
export const createL2WalletClient = (transport) => {
    const l2RpcUrl = cleanEnvValue(process.env.REACT_APP_L2_RPC_URL);
    if (!l2RpcUrl) {
        throw new Error('REACT_APP_L2_RPC_URL 未配置');
    }

    const l2ChainId = Number(cleanEnvValue(process.env.REACT_APP_L2_CHAIN_ID));
    if (!l2ChainId) {
        throw new Error('REACT_APP_L2_CHAIN_ID 未配置');
    }

    return createWalletClient({
        chain: {
            id: l2ChainId,
            name: cleanEnvValue(process.env.REACT_APP_L2_NETWORK_NAME) || 'L2',
            network: 'l2',
            nativeCurrency: {
                decimals: 18,
                name: cleanEnvValue(process.env.REACT_APP_L2_NATIVE_CURRENCY_NAME) || 'ETH',
                symbol: cleanEnvValue(process.env.REACT_APP_L2_NATIVE_CURRENCY_SYMBOL) || 'ETH',
            },
            rpcUrls: {
                default: {
                    http: [l2RpcUrl],
                },
            },
        },
        transport,
    }).extend(walletActionsL2());
};

/**
 * 从 window.ethereum 创建传输层
 * @returns {import('viem').Transport}
 */
export const createEthereumTransport = () => {
    if (typeof window === 'undefined' || !window.ethereum) {
        throw new Error('MetaMask 未安装或 window.ethereum 不可用');
    }

    // 动态导入以避免 SSR 问题
    const { custom } = require('viem');
    return custom(window.ethereum);
};

/**
 * 获取账户地址
 * @param {import('viem').Account} account - 账户
 * @returns {`0x${string}`}
 */
export const getAccountAddress = (account) => {
    return account.address;
};

